import { useEffect, useState, useCallback } from "react";
import api from "../services/api";
import { Button } from "../components/ui/button";
import {
  Activity, RefreshCw, Trash2, CheckCircle2, AlertOctagon, ChevronDown, ChevronRight, Globe,
} from "lucide-react";
import { toast } from "sonner";

const METHOD_COLORS = {
  GET: "bg-emerald-50 text-emerald-700 border-emerald-200",
  POST: "bg-blue-50 text-blue-700 border-blue-200",
  PUT: "bg-amber-50 text-amber-700 border-amber-200",
  PATCH: "bg-violet-50 text-violet-700 border-violet-200",
  DELETE: "bg-red-50 text-red-700 border-red-200",
};

function formatAgo(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const s = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function AdminSystemHealth() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [unreviewed, setUnreviewed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(10);
  const [expandedId, setExpandedId] = useState(null);
  const [clearing, setClearing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/system-health/errors", {
        params: { limit },
      });
      setRows(data.errors || []);
      setTotal(data.total || 0);
      setUnreviewed(data.unreviewed || 0);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || "";
      toast.error(`Failed to load error log${status ? ` (HTTP ${status}: ${detail})` : `: ${detail}`}`);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => { load(); }, [load]);

  const handleMarkReviewed = async (id) => {
    try {
      await api.patch(`/admin/system-health/errors/${id}/review`);
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, reviewed: true } : r)));
      setUnreviewed((u) => Math.max(0, u - 1));
      toast.success("Marked reviewed");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || "";
      toast.error(`Failed: ${status ? `HTTP ${status}: ${detail}` : detail}`);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm("Clear ALL stored error logs? This cannot be undone.")) return;
    setClearing(true);
    try {
      const { data } = await api.delete("/admin/system-health/errors");
      toast.success(`Cleared ${data.deleted} error entries`);
      await load();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || "";
      toast.error(`Clear failed: ${status ? `HTTP ${status}: ${detail}` : detail}`);
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="system-health-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-rose-50 flex items-center justify-center">
            <Activity className="h-5 w-5 text-rose-600" />
          </div>
          <div>
            <h1 className="font-['Outfit'] text-2xl font-bold text-slate-900">System Health</h1>
            <p className="text-sm text-slate-500">
              Last {rows.length}/{total} unhandled backend exceptions · {unreviewed} unreviewed
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="h-9 rounded-lg border border-slate-200 px-3 text-sm font-medium text-slate-700 bg-white"
            data-testid="limit-select"
          >
            <option value={10}>Last 10</option>
            <option value={25}>Last 25</option>
            <option value={50}>Last 50</option>
            <option value={100}>Last 100</option>
          </select>
          <Button
            onClick={load}
            disabled={loading}
            variant="outline"
            size="sm"
            className="h-9 rounded-lg gap-1.5"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            onClick={handleClearAll}
            disabled={clearing || rows.length === 0}
            variant="outline"
            size="sm"
            className="h-9 rounded-lg gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
            data-testid="clear-all-btn"
          >
            <Trash2 className="h-4 w-4" />
            Clear All
          </Button>
        </div>
      </div>

      {/* Empty state */}
      {!loading && rows.length === 0 && (
        <div
          className="bg-white rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-white p-10 text-center"
          data-testid="empty-state"
        >
          <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
          <h2 className="font-['Outfit'] text-lg font-bold text-slate-900 mb-1">All clear</h2>
          <p className="text-sm text-slate-600 max-w-md mx-auto">
            No unhandled exceptions captured in the current window. If the landing-page
            calculator or any admin widget fails, the real HTTP + Python error will land
            here within seconds — no DevTools needed.
          </p>
        </div>
      )}

      {/* Error list */}
      {rows.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100" data-testid="error-list">
          {rows.map((r) => {
            const isOpen = expandedId === r.id;
            const methodCls = METHOD_COLORS[r.method] || METHOD_COLORS.GET;
            return (
              <div
                key={r.id}
                className={`p-4 ${r.reviewed ? "bg-slate-50/50 opacity-60" : ""}`}
                data-testid={`error-row-${r.id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={`mt-0.5 p-1.5 rounded-lg ${r.reviewed ? "bg-slate-100" : "bg-red-50"}`}>
                      {r.reviewed
                        ? <CheckCircle2 className="h-4 w-4 text-slate-400" />
                        : <AlertOctagon className="h-4 w-4 text-red-600" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold border ${methodCls}`}
                          data-testid="error-method"
                        >
                          {r.method}
                        </span>
                        <span
                          className="font-mono text-xs text-slate-700 truncate max-w-full"
                          title={r.path}
                          data-testid="error-path"
                        >
                          {r.path}
                        </span>
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold border ${
                            r.environment === "production"
                              ? "bg-rose-50 text-rose-700 border-rose-200"
                              : "bg-slate-50 text-slate-600 border-slate-200"
                          }`}
                          data-testid="error-env"
                        >
                          <Globe className="h-2.5 w-2.5" />
                          {r.environment}
                        </span>
                        {r.reviewed && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                            Reviewed
                          </span>
                        )}
                      </div>
                      <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                        <span className="font-bold text-sm text-slate-900" data-testid="error-type">
                          {r.exception_type}
                        </span>
                        <span className="text-xs text-slate-400" data-testid="error-time">
                          {formatAgo(r.timestamp)} · {r.timestamp}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 font-mono break-words" data-testid="error-message">
                        {r.message || "(no message)"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {r.traceback_tail && (
                      <Button
                        onClick={() => setExpandedId(isOpen ? null : r.id)}
                        variant="ghost"
                        size="sm"
                        className="h-8 rounded-lg gap-1"
                        data-testid={`toggle-tb-${r.id}`}
                      >
                        {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                        Trace
                      </Button>
                    )}
                    {!r.reviewed && (
                      <Button
                        onClick={() => handleMarkReviewed(r.id)}
                        variant="ghost"
                        size="sm"
                        className="h-8 rounded-lg gap-1 text-emerald-600 hover:bg-emerald-50"
                        data-testid={`mark-reviewed-${r.id}`}
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        Reviewed
                      </Button>
                    )}
                  </div>
                </div>

                {isOpen && r.traceback_tail && (
                  <pre
                    className="mt-3 p-3 bg-slate-900 text-slate-100 rounded-lg text-[11px] font-mono leading-relaxed overflow-x-auto max-h-64 overflow-y-auto"
                    data-testid={`trace-${r.id}`}
                  >
                    {r.traceback_tail}
                  </pre>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="text-[11px] text-slate-400 italic text-center">
        Exceptions are captured by the global FastAPI handler and stored capped at 500. Oldest rolls off automatically.
      </p>
    </div>
  );
}
